import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DatabricksGenieService, DatabricksConfig } from '../../services/databricks-genie.service';
import { GridService } from '../../services/grid.service';
import { catchError, finalize } from 'rxjs/operators';
import { forkJoin, of, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { marked } from 'marked';
import { NgApexchartsModule } from 'ng-apexcharts';

export interface ChatMessage {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  answerText?: string;
  thoughts: string[];
  sqlQuery: string;
  suggestions: string[];
  timestamp: Date;
  status?: string;
  question?: string;
  conversationId?: string;
  statementId?: string;
  chartOptions?: any;
  chartData?: {
    statementId?: string;
    type: string;
    columns: any[];
    rows: any[][];
    categoryIndex: number;
    valueIndex: number;
  };
  tableData?: {
    loading: boolean;
    columns: any[];
    rows: any[];
    rawRows: any[][];
    paginatedRows: any[];
    currentPage?: number;
    pageSize?: number;
  };
  selected?: {
    content: boolean;
    sqlQuery: boolean;
    chart: boolean;
    table: boolean;
    thoughts: boolean;
    suggestions: boolean;
  };
}

@Component({
  selector: 'app-databricks-chat',
  standalone: true,
  imports: [CommonModule, FormsModule, NgApexchartsModule],
  templateUrl: './databricks-chat.component.html',
  styleUrls: ['./databricks-chat.component.scss']
})
export class DatabricksChatComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() config!: DatabricksConfig;
  @ViewChild('chatContainer') private chatContainer!: ElementRef;

  messages: ChatMessage[] = [];
  userInput: string = '';
  isLoading: boolean = false;

  showShareModal: boolean = false;
  availableUsers: any[] = [];
  selectedUserIds: string[] = [];
  userSearchQuery: string = '';
  savedAnalysisIds: string[] = [];

  activeTab: 'saved' | 'shared' = 'saved';
  savedAnalyses: any[] = [];
  sharedAnalyses: any[] = [];
  private sharedUserLabels = new Map<string, string>();
  selectedAnalysisId: string | null = null;
  isReadOnlyAnalysis = false;
  saveFeedback = '';

  private isUserScrolling: boolean = false;
  private token: string | null = null;
  private chatId: string | null = null;
  private authSubscription: Subscription | null = null;
  private mutationObserver?: MutationObserver;
  user: any;

  constructor(
    private databricksService: DatabricksGenieService,
    private gridService: GridService
  ) { }

  ngOnInit(): void {
    if (!this.config && (environment as any).databricks) {
      this.config = (environment as any).databricks;
    }

    if (this.config && this.config.baseUrl && this.config.spaceId) {
      this.authenticate();
    } else {
      console.error('DatabricksChatComponent requires a valid DatabricksConfig input or "databricks" object in environment.ts');
    }

    this.user = this.getCurrentUser();
    if (this.user) {
      this.loadAnalyses();
    }
  }

  loadAnalyses(): void {
    const userId = this.getCurrentUserId();
    if (!userId) return;

    this.gridService.getScopeData('DBAIDATA', 'READ', { ViewCode: 'DBAIDATA.DEFAULT', Filter: `{ CreationUserCode: "${userId}" }`, Sort: '{ LastEditDate: -1 }' }).subscribe((res: any) => {
      if (res && res.data) {
        this.savedAnalyses = res.data;
      }
    });
    this.gridService.getScopeData('DBAIDATASHARE', 'READ', { ViewCode: 'DBAIDATASHARE.DEFAULT', Filter: `{ $or: [{ RecipientUserCode: "${userId}"}, { CreationUserCode: "${userId}" }] }`, Sort: '{ LastEditDate: -1 }' }).subscribe((res: any) => {
      if (res && res.data) {
        const groupedMap = new Map<string, any>();

        res.data.forEach((item: any) => {
          const analysisIdValue = item.AnalysisId?.value || item.AnalysisId || item._id || item.Id || item.id;

          if (!groupedMap.has(analysisIdValue)) {
            groupedMap.set(analysisIdValue, {
              ...item,
              SentTo: [],
              ReceivedFrom: []
            });
          }

          const groupedItem = groupedMap.get(analysisIdValue);

          const isSender = item.CreationUserCode?.value === userId;
          const isRecipient = item.RecipientUserCode?.value === userId;

          if (isSender && item.RecipientUserCode) {
            const name = `${item.RecipientUserCode.attribute1} ${item.RecipientUserCode.attribute2}` || item.RecipientUserCode.text || 'Utente';
            if (!groupedItem.SentTo.includes(name)) groupedItem.SentTo.push(name);
          } else if (isRecipient && item.CreationUserCode) {
            const name = `${item.CreationUserCode.attribute1} ${item.CreationUserCode.attribute2}` || item.CreationUserCode.text || 'Utente';
            if (!groupedItem.ReceivedFrom.includes(name)) groupedItem.ReceivedFrom.push(name);
          }
        });

        this.sharedAnalyses = Array.from(groupedMap.values());
      }
    });
  }

  /*private loadSharedUserLabels(items: any[]): void {
    const userIds = Array.from(new Set(
      (items || [])
        .flatMap(item => [item?.CreationUserCode, item?.RecipientUserCode])
        .map(value => this.getScopeValue(value))
        .filter(Boolean)
    ));

    if (userIds.length === 0) return;

    this.gridService.getSharedAnalysisParticipants().subscribe({
      next: (res: any) => {
        const users = Array.isArray(res) ? res : (res?.data || []);
        users.forEach((user: any) => {
          const userId = this.getScopeValue(user?._id || user?.Id || user?.id);
          const label = user?.Username || user?.username || user?.Email || user?.email;
          if (userId && label) this.sharedUserLabels.set(userId, String(label));
        });
      },
      error: error => console.warn('Impossibile risolvere i partecipanti delle analisi condivise', error)
    });
  }*/

  private getCurrentUser(): any {
    try {
      // Drake salva l'utente autenticato nella chiave `user`.
      // `loggedUser` può contenere dati precedenti e produrre filtri errati
      // quando si passa da un account all'altro.
      const rawUser = localStorage.getItem('user') || localStorage.getItem('loggedUser');
      return rawUser ? JSON.parse(rawUser) : null;
    } catch {
      return null;
    }
  }

  private getCurrentUserId(): string {
    const currentUser = this.user || this.getCurrentUser();
    return String(currentUser?.id ?? currentUser?._id ?? currentUser?.Id ?? '');
  }

  getAnalysisId(item: any): string {
    return item.AnalysisId?.value || item.AnalysisId?.text || item.AnalysisId || item._id || item.Id || item.id || '';
  }

  getScopeValue(value: any): string {
    return String(value?.value ?? value?._id ?? value?.id ?? value ?? '');
  }

  getScopeText(value: any, fallback = 'Utente'): string {
    const valueCode = this.getScopeValue(value);
    const resolvedText = value?.text ?? value?.Username ?? value?.username ?? value?.Email ?? value?.email;
    if (resolvedText && String(resolvedText) !== '#N/A#') return String(resolvedText);
    return this.sharedUserLabels.get(valueCode) || (valueCode || fallback);
  }

  isShareCreatedByCurrentUser(item: any): boolean {
    return this.getScopeValue(item?.CreationUserCode) === this.getCurrentUserId();
  }

  getShareParticipantLabel(item: any): string {
    return this.isShareCreatedByCurrentUser(item)
      ? `Condiviso con: ${this.getScopeText(item?.RecipientUserCode)}`
      : `Ricevuto da: ${this.getScopeText(item?.CreationUserCode)}`;
  }

  loadAnalysis(item: any, readOnly = false): void {
    const analysisId = this.getAnalysisId(item);
    if (!analysisId) return;

    this.selectedAnalysisId = analysisId;
    this.isReadOnlyAnalysis = readOnly;

    this.gridService.getScopeData('DBAIDATA', 'READ', { ViewCode: 'DBAIDATA.FULL', Filter: `{ _id: "${analysisId}" }` }).subscribe((res: any) => {
      if (res && res.data && res.data.length > 0) {
        const data = res.data[0];

        // Svuotiamo i messaggi correnti per far vedere l'analisi ricaricata
        this.messages = [];

        if (data.OriginalPrompt) {
          this.messages.push({
            role: 'user',
            content: data.OriginalPrompt,
            timestamp: this.parseDate(data.TimeStamp || data.Timestamp) as Date,
            thoughts: [],
            sqlQuery: '',
            suggestions: []
          });
        }

        const storedChartData = this.normalizeChartData(data.ChartData);
        let loadedChartData = storedChartData;
        let loadedChartOptions: any = null;
        if (storedChartData && Array.isArray(storedChartData.rows)) {
          loadedChartOptions = this.buildChartOptions(storedChartData);
        } else if (storedChartData?.series && storedChartData?.chart) {
          // Supporta anche eventuali record salvati con le sole opzioni ApexCharts.
          loadedChartOptions = storedChartData;
          loadedChartData = this.chartDataFromOptions(storedChartData);
        }
        if (loadedChartOptions && loadedChartOptions.yaxis && loadedChartOptions.yaxis.labels) {
          loadedChartOptions.yaxis.decimalsInFloat = 0;
          loadedChartOptions.yaxis.labels.formatter = (value: number) => this.formatChartValue(value);
        }

        const loadedTableData = this.normalizeTableData(data.TableData);
        const loadedMessage: ChatMessage = {
          id: data.MessageId || data._id,
          role: 'assistant',
          content: data.Content ? marked.parse(data.Content) as string : (data.AnswerText ? marked.parse(data.AnswerText) as string : ''),
          answerText: data.AnswerText || data.Content || '',
          timestamp: this.parseDate(data.TimeStamp || data.Timestamp) as Date,
          thoughts: data.Thoughts || [],
          sqlQuery: data.SqlQuery || '',
          chartOptions: loadedChartOptions,
          chartData: Array.isArray(loadedChartData?.rows) ? loadedChartData : undefined,
          tableData: loadedTableData,
          status: 'COMPLETED',
          suggestions: data.SuggestedQuestions || [],
          question: data.OriginalPrompt,
          conversationId: data.ConversationId,
          statementId: data.StatementId,
          selected: {
            content: false,
            sqlQuery: false,
            chart: false,
            table: false,
            thoughts: false,
            suggestions: false
          }
        };
        this.messages.push(loadedMessage);
      }
    });
  }

  private normalizeTableData(tableData: any): ChatMessage['tableData'] {
    if (!tableData) return undefined;
    const rawRows = tableData.rawRows || tableData.RawRows || tableData.rows || tableData.Rows || [];
    const rows = tableData.rows || tableData.Rows || rawRows;
    const columns = tableData.columns || tableData.Columns || [];
    const pageSize = tableData.pageSize || 15;
    const currentPage = tableData.currentPage || 1;
    return {
      loading: false,
      columns,
      rows,
      rawRows,
      paginatedRows: rows.slice(0, pageSize),
      currentPage,
      pageSize
    };
  }

  // Metodo helper per correggere eventuali discrepanze di fuso orario dal server
  parseDate(dateStr: any): Date | string {
    if (!dateStr) return '';
    // Se la data arriva come stringa e non ha la 'Z', Angular potrebbe interpretarla male
    // Puoi regolare qui il comportamento, per esempio forzandola in UTC o sottraendo ore.
    let d = new Date(dateStr.replace('T', ' ').replace('Z', ''));

    // Esempio: se vedi sempre 4 ore in più, puoi de-commentare la riga sotto per sottrarle forzatamente:
    // d.setHours(d.getHours() - 4);

    return d;
  }

  startNewChat(): void {
    this.messages = [];
    this.selectedAnalysisId = null;
    this.chatId = null;
    this.isReadOnlyAnalysis = false;
  }

  ngAfterViewInit(): void {
    if (this.chatContainer) {
      this.mutationObserver = new MutationObserver(() => {
        this.scrollToBottom();
      });
      this.mutationObserver.observe(this.chatContainer.nativeElement, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
  }

  ngOnDestroy(): void {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  private authenticate(): void {
    this.isLoading = true;
    this.authSubscription = this.databricksService.login(this.config)
      .pipe(
        finalize(() => this.isLoading = false),
        catchError(err => {
          console.error('Authentication failed', err);
          this.messages.push({ role: 'assistant', content: 'Errore di autenticazione con Databricks.', timestamp: new Date(), thoughts: [], sqlQuery: '', suggestions: [] });
          return of(null);
        })
      )
      .subscribe(res => {
        if (res && res.access_token) {
          this.token = res.access_token;
          this.messages.push({ role: 'assistant', content: 'Connesso a DrakeBrain! Come posso aiutarti oggi?', timestamp: new Date(), thoughts: [], sqlQuery: '', suggestions: [] });
        }
      });
  }

  sendMessage(question?: string): void {
    const textToSend = question ? question.trim() : this.userInput.trim();
    if (!textToSend || !this.token || this.isLoading || this.isReadOnlyAnalysis) return;

    this.isUserScrolling = false;
    this.selectedAnalysisId = null;
    this.isReadOnlyAnalysis = false;
    this.messages.push({ role: 'user', content: textToSend, timestamp: new Date(), thoughts: [], sqlQuery: '', suggestions: [] });
    this.userInput = '';
    this.isLoading = true;

    if (!this.chatId) {
      this.databricksService.startConversation(this.config, this.token, textToSend).subscribe({
        next: (res) => {
          if (res.conversation && res.conversation.id) {
            this.chatId = res.conversation.id;
          }
          this.pollMessage(res.conversation?.id || this.chatId!, res.message_id || res.id);
        },
        error: (err) => this.handleError(err)
      });
    } else {
      this.databricksService.nextQuestion(this.config, this.token, this.chatId, textToSend).subscribe({
        next: (res) => {
          this.pollMessage(this.chatId!, res.message_id || res.id);
        },
        error: (err) => this.handleError(err)
      });
    }
  }

  private pollMessage(chatId: string, messageId: string): void {
    if (!messageId || !chatId) {
      this.isLoading = false;
      return;
    }

    this.databricksService.getMessage(this.config, this.token!, chatId, messageId)
      .subscribe({
        next: (res) => {
          let currentMsg = this.messages.find(m => m.id === messageId);
          if (!currentMsg) {
            currentMsg = { role: 'assistant', content: '', timestamp: new Date(), id: messageId, thoughts: [], sqlQuery: '', suggestions: [], status: res?.status };
            this.messages.push(currentMsg);
          } else if (res?.status) {
            currentMsg.status = res.status;
          }

          this.parseDatabricksResponse(res, currentMsg, chatId, messageId);

          if (res && res.status === 'COMPLETED') {
            this.isLoading = false;
          } else if (res && (res.status === 'FAILED' || res.status === 'CANCELED')) {
            currentMsg.content += '\n\n❌ Errore: la richiesta ha fallito o è stata annullata.';
            this.isLoading = false;
          } else {
            setTimeout(() => this.pollMessage(chatId, messageId), 2000);
          }
        },
        error: (err) => {
          this.handleError(err);
        }
      });
  }

  private parseDatabricksResponse(res: any, currentMsg: ChatMessage, chatId: string, messageId: string): void {
    let hasChart = false;
    currentMsg.question = currentMsg.question || this.getLastUserQuestion();
    currentMsg.conversationId = currentMsg.conversationId || chatId;
    currentMsg.statementId = currentMsg.statementId || res?.query_result?.statement_id;
    currentMsg.selected = currentMsg.selected || this.emptySelection();

    if (res && res.attachments && Array.isArray(res.attachments)) {
      res.attachments.forEach((att: any) => {
        if (att.query) {
          if (att.query.thoughts && Array.isArray(att.query.thoughts)) {
            att.query.thoughts.forEach((t: any) => {
              if (t.content && !currentMsg.thoughts.includes(t.content)) {
                currentMsg.thoughts.push(t.content);
              }
            });
          }
          if (att.query.query) {
            currentMsg.sqlQuery = att.query.query;
          }
        }
        if (att.text && att.text.content) {
          currentMsg.answerText = att.text.content;
          currentMsg.content = marked.parse(att.text.content) as string;
        }
        if (att.suggested_questions && Array.isArray(att.suggested_questions.questions)) {
          currentMsg.suggestions = att.suggested_questions.questions;
        }
        if (att.viz && att.viz.query_attachment_id && !currentMsg.chartOptions) {
          hasChart = true;
          this.fetchChartData(chatId, messageId, att.viz.query_attachment_id, currentMsg);
        }
      });
    }

    // Se non è stato generato un grafico e c'è uno statement id, recuperiamo i dati testuali per una tabella
    if (!hasChart && res && res.query_result && res.query_result.statement_id && !currentMsg.tableData && !currentMsg.chartOptions) {
      this.fetchStatementData(res.query_result.statement_id, currentMsg);
    }
  }

  private fetchStatementData(statementId: string, currentMsg: ChatMessage) {
    currentMsg.statementId = statementId;
    currentMsg.tableData = { loading: true, columns: [], rows: [], rawRows: [], paginatedRows: [], currentPage: 1, pageSize: 15 };

    this.databricksService.getStatementResult(this.config, this.token!, statementId).subscribe({
      next: (data) => {
        if (data && data.status && data.status.state === 'SUCCEEDED' && data.manifest && data.manifest.schema && data.result) {
          const columns = data.manifest.schema.columns || [];
          const rawRows = data.result.data_array || [];

          // Formattatori
          const numberFormat = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2 });

          const formattedRows = rawRows.map((row: any[]) => {
            return row.map((cell: any, index: number) => {
              if (cell === null || cell === undefined) return '';

              const typeName = columns[index]?.type_name?.toUpperCase() || '';

              if (typeName === 'DATE') {
                return new Date(cell).toLocaleDateString('it-IT');
              }
              if (typeName === 'TIMESTAMP') {
                return new Date(cell).toLocaleString('it-IT');
              }
              if (['DOUBLE', 'FLOAT', 'DECIMAL', 'LONG', 'INT', 'BIGINT', 'INTEGER', 'SHORT', 'TINYINT'].includes(typeName)) {
                const num = Number(cell);
                return isNaN(num) ? cell : numberFormat.format(num);
              }

              return cell;
            });
          });

          currentMsg.tableData = {
            loading: false,
            columns: columns,
            rows: formattedRows,
            rawRows: rawRows,
            paginatedRows: formattedRows.slice(0, 15),
            currentPage: 1,
            pageSize: 15
          };
        } else {
          currentMsg.tableData = undefined;
        }
      },
      error: (err) => {
        console.error("Failed to fetch statement data", err);
        currentMsg.tableData = undefined;
      }
    });
  }

  private fetchChartData(chatId: string, messageId: string, attachmentId: string, currentMsg: ChatMessage) {
    currentMsg.chartOptions = { loading: true };

    this.databricksService.getAttachment(this.config, this.token!, chatId, messageId, attachmentId).subscribe({
      next: (data) => {
        const resp = data && data.statement_response ? data.statement_response : data;

        if (resp && resp.result && resp.result.data_array && resp.manifest && resp.manifest.schema) {
          const columns = resp.manifest.schema.columns;
          const rows = resp.result.data_array;

          const categoryColIndex = columns.findIndex((c: any) => c.type_name === 'STRING' || c.type_name === 'VARCHAR');
          const catIndex = categoryColIndex >= 0 ? categoryColIndex : 0;

          const numericColIndex = columns.findIndex((c: any) => c.type_name !== 'STRING' && c.type_name !== 'VARCHAR' && c.type_name !== 'DATE' && c.type_name !== 'TIMESTAMP');
          const numIndex = numericColIndex >= 0 ? numericColIndex : 1;

          if (rows.length > 0 && numIndex < columns.length) {
            currentMsg.chartData = {
              statementId: resp.statement_id,
              type: 'bar',
              columns,
              rows,
              categoryIndex: catIndex,
              valueIndex: numIndex
            };
            currentMsg.chartOptions = this.buildChartOptions(currentMsg.chartData);
          } else {
            currentMsg.chartOptions = null;
          }
        } else {
          currentMsg.chartOptions = null;
        }
      },
      error: (err) => {
        console.error("Failed to fetch viz data", err);
        currentMsg.chartOptions = null;
      }
    });
  }

  private buildChartOptions(chartData: any): any {
    const columns = chartData.columns || [];
    const rows = chartData.rows || [];

    // Trova le colonne categoriche (X) e numeriche (Y)
    const numericIndices: number[] = [];
    const categoryIndices: number[] = [];

    columns.forEach((col: any, index: number) => {
      const typeName = col.type_name?.toUpperCase() || '';
      if (['DOUBLE', 'FLOAT', 'DECIMAL', 'LONG', 'INT', 'BIGINT', 'INTEGER', 'SHORT', 'TINYINT'].includes(typeName)) {
        numericIndices.push(index);
      } else {
        categoryIndices.push(index);
      }
    });

    // Fallback se mancano tipologie chiare
    if (categoryIndices.length === 0 && columns.length > 0) categoryIndices.push(0);
    if (numericIndices.length === 0 && columns.length > 1) numericIndices.push(columns.length - 1);

    // Costruiamo le etichette per l'asse X unendo le colonne categoriche
    const categories = rows.map((row: any[]) => {
      return categoryIndices.map(i => {
        let val = row[i];
        if (columns[i]?.type_name?.toUpperCase() === 'TIMESTAMP' || columns[i]?.type_name?.toUpperCase() === 'DATE') {
          const d = new Date(val);
          if (!isNaN(d.getTime())) return d.toLocaleDateString('it-IT');
        }
        return val || 'N/D';
      }).join(' - ');
    });

    // Costruiamo una serie per ogni colonna numerica trovata
    const series = numericIndices.map(i => {
      return {
        name: columns[i]?.name || `Serie ${i}`,
        data: rows.map((row: any[]) => {
          const val = row[i];
          return val !== null && val !== undefined ? Number(val) : 0;
        })
      };
    });

    const customColor = getComputedStyle(document.documentElement).getPropertyValue('--custom-color').trim() || '#2563eb';
    const colors = [customColor, '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316'];

    return {
      series: series,
      chart: { type: chartData.type || 'bar', height: 350, toolbar: { show: true } },
      plotOptions: { bar: { horizontal: false, borderRadius: 2, columnWidth: '60%' } },
      dataLabels: { enabled: false },
      stroke: { show: true, width: 2, colors: ['transparent'] },
      xaxis: {
        categories,
        labels: {
          rotate: -45,
          trim: true,
          maxHeight: 120,
          style: { fontSize: '11px' }
        }
      },
      yaxis: {
        decimalsInFloat: 0,
        labels: {
          formatter: (value: number) => this.formatChartValue(value)
        }
      },
      tooltip: {
        y: {
          formatter: (value: number) => this.formatChartValue(value)
        }
      },
      legend: { position: 'top', horizontalAlign: 'center' },
      colors: colors
    };
  }

  private formatChartValue(value: any): string {
    if (value === null || value === undefined || value === '') return '';

    const numericValue = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numericValue)) return String(value);

    return numericValue.toLocaleString('it-IT', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }

  private emptySelection(): NonNullable<ChatMessage['selected']> {
    return {
      content: false,
      sqlQuery: false,
      chart: false,
      table: false,
      thoughts: false,
      suggestions: false
    };
  }

  private getSelectableParts(msg: ChatMessage): string[] {
    const parts: string[] = [];
    if (msg.content || msg.answerText) parts.push('content');
    if (msg.sqlQuery) parts.push('sqlQuery');
    if (msg.chartData || msg.chartOptions) parts.push('chart');
    if (msg.tableData && !msg.tableData.loading) parts.push('table');
    if (msg.thoughts?.length) parts.push('thoughts');
    if (msg.suggestions?.length) parts.push('suggestions');
    return parts;
  }

  hasAnySelection(): boolean {
    return this.messages.some(message => {
      if (message.role !== 'assistant' || !message.selected) return false;
      return Object.values(message.selected).some(Boolean);
    });
  }

  toggleAll(msg: ChatMessage): void {
    msg.selected = msg.selected || this.emptySelection();
    const parts = this.getSelectableParts(msg) as Array<keyof NonNullable<ChatMessage['selected']>>;
    const selectAll = !parts.length || !parts.every(part => !!msg.selected?.[part]);
    parts.forEach(part => {
      if (msg.selected) msg.selected[part] = selectAll;
    });
  }

  isAllSelected(msg: ChatMessage): boolean {
    const parts = this.getSelectableParts(msg) as Array<keyof NonNullable<ChatMessage['selected']>>;
    return parts.length > 0 && !!msg.selected && parts.every(part => !!msg.selected?.[part]);
  }

  saveSelectedToDb(): void {
    const userId = this.getCurrentUserId();
    const selectedMessages = this.messages.filter(message =>
      message.role === 'assistant' && message.selected && this.getSelectableParts(message).some(part => !!message.selected?.[part as keyof NonNullable<ChatMessage['selected']>])
    );

    if (!userId || selectedMessages.length === 0) {
      this.saveFeedback = 'Seleziona almeno una parte della risposta.';
      return;
    }

    const requests = selectedMessages.map(message => {
      const selected = message.selected || this.emptySelection();
      const record: any = {
        OriginalPrompt: message.question || this.getLastUserQuestion() || '',
        CreationUserCode: userId,
        TimeStamp: new Date().toISOString(),
        Title: (message.question || message.answerText || 'Analisi Genie').slice(0, 255),
        ...(message.id ? { MessageId: message.id } : {}),
        ...(selected.content ? { Content: message.answerText || message.content } : {}),
        ...(selected.thoughts ? { Thoughts: message.thoughts } : {}),
        ...(selected.sqlQuery ? { SqlQuery: message.sqlQuery } : {}),
        ...(selected.chart && this.getChartDataForSave(message)
          ? { ChartData: this.getChartDataForSave(message) }
          : {}),
        ...(selected.table && message.tableData ? { TableData: this.serializeTableData(message.tableData) } : {})
      };
      return this.gridService.saveScopeData('DBAIDATA', 'UPSERT', record, false);
    });

    this.saveFeedback = 'Salvataggio in corso...';
    forkJoin(requests).subscribe({
      next: () => {
        this.saveFeedback = selectedMessages.length === 1 ? 'Risposta salvata.' : `${selectedMessages.length} risposte salvate.`;
        this.loadAnalyses();
      },
      error: (error) => {
        console.error('Errore nel salvataggio dell’analisi', error);
        this.saveFeedback = 'Salvataggio non riuscito.';
      }
    });
  }

  private serializeTableData(tableData: ChatMessage['tableData']): any {
    if (!tableData) return null;
    return {
      columns: tableData.columns,
      rawRows: tableData.rawRows || tableData.rows,
      rows: tableData.rows,
      pageSize: tableData.pageSize || 15
    };
  }

  private normalizeChartData(chartData: any): any {
    if (!chartData) return undefined;

    if (typeof chartData === 'string') {
      try {
        return this.normalizeChartData(JSON.parse(chartData));
      } catch {
        return undefined;
      }
    }

    if (typeof chartData !== 'object') return undefined;

    // Alcune risposte possono restituire il valore dell'OBJECT dentro un wrapper.
    if (chartData.value !== undefined && chartData.rows === undefined && chartData.columns === undefined) {
      return this.normalizeChartData(chartData.value);
    }
    if (chartData.Value !== undefined && chartData.rows === undefined && chartData.columns === undefined) {
      return this.normalizeChartData(chartData.Value);
    }
    if (chartData.data !== undefined && chartData.rows === undefined && chartData.columns === undefined && !chartData.series) {
      return this.normalizeChartData(chartData.data);
    }

    return chartData;
  }

  private getChartDataForSave(message: ChatMessage): any {
    const chartData = this.normalizeChartData(message.chartData);
    if (chartData && Array.isArray(chartData.rows)) return chartData;

    return this.chartDataFromOptions(message.chartOptions);
  }

  private chartDataFromOptions(chartOptions: any): any {
    if (!chartOptions?.series?.length || !Array.isArray(chartOptions.xaxis?.categories)) {
      return undefined;
    }

    const series = chartOptions.series[0];
    const categories = chartOptions.xaxis.categories;
    const values = Array.isArray(series.data) ? series.data : [];
    if (!categories.length || !values.length) return undefined;

    return {
      type: chartOptions.chart?.type || 'bar',
      columns: [
        {
          name: chartOptions.xaxis?.title?.text || 'Categoria',
          position: 0,
          type_name: 'STRING',
          type_text: 'STRING'
        },
        {
          name: series.name || 'Valore',
          position: 1,
          type_name: 'DOUBLE',
          type_text: 'DOUBLE'
        }
      ],
      rows: categories.map((category: any, index: number) => [category, values[index]]),
      categoryIndex: 0,
      valueIndex: 1
    };
  }

  private createClientId(): string {
    return typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `analysis-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  get filteredUsers(): any[] {
    const query = this.userSearchQuery.trim().toLowerCase();
    if (!query) return this.availableUsers;
    return this.availableUsers.filter(user =>
      [user.FirstName, user.LastName, user.Email].filter(Boolean)
        .some(value => String(value).toLowerCase().includes(query))
    );
  }

  openShareModal(analysisId?: string): void {
    this.selectedAnalysisId = analysisId || this.selectedAnalysisId;
    this.selectedUserIds = [];
    this.userSearchQuery = '';
    this.showShareModal = true;
    const currentUserId = this.getCurrentUserId();
    this.gridService.getScopeData('USERS', 'READ', {
      ViewCode: 'USERS.DEFAULT',
      Filter: `{ _id: { $ne: "${currentUserId}" } }`,
      Sort: '{ LastName: 1, FirstName: 1 }'
    }).subscribe({
      next: (res: any) => this.availableUsers = res?.data || [],
      error: (error: any) => {
        console.error('Impossibile caricare gli utenti per la condivisione', error);
        this.availableUsers = [];
      }
    });
  }

  cancelShare(): void {
    this.showShareModal = false;
    this.selectedUserIds = [];
    this.userSearchQuery = '';
  }

  toggleUserSelection(userId: string): void {
    this.selectedUserIds = this.selectedUserIds.includes(userId)
      ? this.selectedUserIds.filter(id => id !== userId)
      : [...this.selectedUserIds, userId];
  }

  isUserSelected(userId: string): boolean {
    return this.selectedUserIds.includes(userId);
  }

  confirmShare(): void {
    const analysisId = this.selectedAnalysisId;
    const creatorId = this.getCurrentUserId();
    if (!analysisId || !creatorId || this.selectedUserIds.length === 0) return;

    const requests = this.selectedUserIds.map(recipientId => this.gridService.saveScopeData(
      'DBAIDATASHARE',
      'UPSERT',
      {
        AnalysisId: analysisId,
        RecipientUserCode: recipientId,
        CreationUserCode: creatorId,
        Title: 'Condivisione analisi'
      },
      false
    ));

    forkJoin(requests).subscribe({
      next: () => {
        this.cancelShare();
        this.loadAnalyses();
      },
      error: error => console.error('Errore nella condivisione dell’analisi', error)
    });
  }

  private handleError(err: any): void {
    console.error('API Error', err);
    this.messages.push({ role: 'assistant', content: 'Si è verificato un errore durante la comunicazione con Databricks.', timestamp: new Date(), thoughts: [], sqlQuery: '', suggestions: [] });
    this.isLoading = false;
  }

  getStatusDescription(status: string | undefined): string {
    if (!status) return 'Sto elaborando...';

    const map: any = {
      'FETCHING_METADATA': 'Recupero metadati dalle sorgenti dati...',
      'FILTERING_CONTEXT': 'Determinazione del contesto rilevante...',
      'ASKING_AI': 'In attesa di risposta dall\'intelligenza artificiale...',
      'PENDING_WAREHOUSE': 'In attesa del warehouse per eseguire la query...',
      'EXECUTING_QUERY': 'Esecuzione della query SQL...',
      'SUBMITTED': 'Richiesta inviata...',
      'COMPLETED': 'Elaborazione completata.'
    };
    return map[status] || 'Sto elaborando...';
  }

  changeTablePage(msg: ChatMessage, delta: number): void {
    if (!msg.tableData) return;
    const newPage = (msg.tableData.currentPage || 1) + delta;
    const maxPage = this.getTableMaxPage(msg);

    if (newPage >= 1 && newPage <= maxPage) {
      msg.tableData.currentPage = newPage;
      const size = msg.tableData.pageSize || 15;
      const start = (newPage - 1) * size;
      msg.tableData.paginatedRows = msg.tableData.rows.slice(start, start + size);
    }
  }

  getTableMaxPage(msg: ChatMessage): number {
    if (!msg.tableData) return 1;
    const size = msg.tableData.pageSize || 15;
    return Math.ceil(msg.tableData.rows.length / size) || 1;
  }

  onScroll(): void {
    if (!this.chatContainer) return;
    const el = this.chatContainer.nativeElement;
    // Considera l'utente alla fine se è a meno di 50px dal fondo
    const isAtBottom = Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight) < 50;
    this.isUserScrolling = !isAtBottom;
  }

  private scrollToBottom(): void {
    if (this.isUserScrolling || !this.chatContainer) return;
    try {
      this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
    } catch (err) { }
  }

  private getLastUserQuestion(): string | undefined {
    return [...this.messages].reverse().find(message => message.role === 'user')?.content;
  }
}
